import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CollectionNavigationService } from '../collection-layout/collection-navigation.service';
import { EditStateCoordinator } from './edit-state-coordinator.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';

@Component({
  selector: 'zag-entity-edit-nav-previous-next',
  template: `
    @if (nav()) {
      <div class="zag-entity-nav" role="navigation" [attr.aria-label]="'entityNav.ariaLabel' | translate">
        <span class="zag-entity-nav__position">
          {{ 'entityNav.position' | translate: { n: nav()!.index, total: nav()!.total } }}
        </span>

        <button
          type="button"
          zagButton
          variant="ghost"
          [disabled]="!nav()!.prev || isNavigating()"
          [attr.aria-label]="'entityNav.previous' | translate"
          (click)="navigateTo(nav()!.prev)"
        >
          <span zagIcon="chevron_left"></span>
          {{ 'entityNav.previous' | translate }}
        </button>

        <button
          type="button"
          zagButton
          variant="ghost"
          [disabled]="!nav()!.next || isNavigating()"
          [attr.aria-label]="'entityNav.next' | translate"
          (click)="navigateTo(nav()!.next)"
        >
          {{ 'entityNav.next' | translate }}
          <span zagIcon="chevron_right"></span>
        </button>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ZagButton, ZagIcon],
})
export class EntityEditNavPreviousNextComponent {
  readonly collectionKey = input.required<string>();

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly navService = inject(CollectionNavigationService);
  private readonly coordinator = inject(EditStateCoordinator, { optional: true });
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);

  protected readonly isNavigating = signal(false);

  private readonly routeParams = toSignal(this.route.paramMap);
  private readonly currentId = computed(() => this.routeParams()?.get('id') ?? null);

  protected readonly nav = computed(() => {
    const id = this.currentId();
    const key = this.collectionKey();
    if (!id || !key) return null;
    return this.navService.getNeighbours(key, id);
  });

  protected async navigateTo(targetId: string | null): Promise<void> {
    if (!targetId || this.isNavigating()) return;

    const isDirty = this.coordinator?.hasDirtyChanges() ?? false;

    if (isDirty) {
      const confirmed = await this.confirmDialog.confirm({
        title: this.translate.instant('entityNav.unsavedTitle'),
        message: this.translate.instant('entityNav.unsavedMessage'),
        confirmText: this.translate.instant('entityNav.discardAndContinue'),
        destructive: false,
      });
      if (!confirmed) return;
    }

    this.isNavigating.set(true);
    const currentUrl = this.router.url;
    const currentId = this.currentId();
    if (!currentId) {
      this.isNavigating.set(false);
      return;
    }

    const targetUrl = currentUrl.replace(currentId, targetId);
    void this.router.navigateByUrl(targetUrl).finally(() => this.isNavigating.set(false));
  }
}
